/**
 * tradeMatchSignal — "the app just found you a trade" broadcast.
 *
 * The Map owns match discovery; the Footer's Trades icon and the map's toast
 * both need to react to it. They're siblings under Layout, so rather than lift
 * match state into a provider that nothing else wants, this is a tiny pub/sub.
 *
 * The rule it enforces: motion means NEW. Re-rendering with the same matches,
 * or matches going away, must not re-trigger the animation — that's what turns
 * a signal into wallpaper (same reason the nav badges lost their inventory
 * counts).
 */

const listeners = new Set();

/**
 * Should a fresh count trigger the celebration?
 * Only an increase counts. First-ever load counts if there's anything to show.
 */
export const shouldPulseForMatches = (prevCount, nextCount) => {
  const prev = Number.isFinite(prevCount) ? prevCount : 0;
  const next = Number.isFinite(nextCount) ? nextCount : 0;
  return next > prev;
};

/** Subscribe to match announcements. Returns an unsubscribe fn. */
export const subscribeTradeMatches = (fn) => {
  if (typeof fn !== "function") return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * Announce the current match count. Fires listeners only when it grew.
 * @returns {boolean} whether listeners were notified
 */
export const announceTradeMatches = (count, prevCount) => {
  if (!shouldPulseForMatches(prevCount, count)) return false;
  const delta = Math.max(0, (count || 0) - (prevCount || 0));
  listeners.forEach((fn) => {
    try {
      fn({ count: count || 0, delta });
    } catch {
      // A broken listener must not take down the announcer.
    }
  });
  return true;
};

/** Test seam — drop every listener. */
export const __resetTradeMatchListeners = () => listeners.clear();
